import { Box, Snackbar, Alert } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useEffect, useState } from "react";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import { useAppSelector } from "src/app/hooks";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { useCardForm } from "src/hooks/useCardForm";

interface AcuteGvhdClinicalScoringData {
  AGS_ID: string;
  CTIME: Date;
  PROCESS_TIME: Date;
  CUSER: string;
  SKIN: string;
  LIVER: string;
  UPPER_GIS: string;
  LOWER_GIS: string;
  SCORE: number;
}

interface AcuteGvhdClinicalScoringProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id?: string) => Promise<void | string | null>;
  handleChange: (field: keyof AcuteGvhdClinicalScoringData, value: any) => void;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function AcuteGvhdClinicalScoringCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  isSaving,
  extraAccess,
}: AcuteGvhdClinicalScoringProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "ACUTE_GHVD_CLINICAL_SCORING",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof AcuteGvhdClinicalScoringData, value);
      });
    },
  });

  const typedFormData = formData as AcuteGvhdClinicalScoringData;

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      parentHandleChange(key as keyof AcuteGvhdClinicalScoringData, value);
    });
  }, [formData, parentHandleChange]);

  const [score, setScore] = useState(0);

  const handleScoreCalculate = () => {
    if (!typedFormData) return;

    const scoreFields = [
      "SKIN_XVALUE",
      "LIVER_XVALUE",
      "UPPER_GIS_XVALUE",
      "LOWER_GIS_XVALUE",
    ];

    const score = scoreFields.reduce((sum, field) => {
      const value = Number((typedFormData as any)[field]) || 0;
      return sum + value;
    }, 0);

    setScore(score);
    handleChange("SCORE", score);
  };

  useEffect(() => {
    handleScoreCalculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    typedFormData?.SKIN,
    typedFormData?.LIVER,
    typedFormData?.UPPER_GIS,
    typedFormData?.LOWER_GIS
  ]);


  return (
    <>
      <Snackbar
        open={!!error?.open}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error?.message}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
            <OtherPrivilegeGuard
              extraAccess={!!extraAccess}
              fallback={
                <ByzMuiSaveButton
                  disabled
                  tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
                />
              }
            >
              <ByzMuiSaveButton
                onClick={() => save(typedFormData.AGS_ID)}
                loading={isSaving}
              />
            </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("acute-ghvd-clinical-scoring.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  onChange={(e) => handleChange("CUSER", e.target.value)}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("acute-ghvd-clinical-scoring.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("acute-ghvd-clinical-scoring.skin")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("acute-ghvd-clinical-scoring.skin")}
                  mcpId={cardData.mcpConfig.SKIN.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("SKIN", selected);
                    }
                  }}
                  value={selectedMCPs.SKIN}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.SKIN.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("acute-ghvd-clinical-scoring.liver")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("acute-ghvd-clinical-scoring.liver")}
                  mcpId={cardData.mcpConfig.LIVER.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("LIVER", selected);
                    }
                  }}
                  value={selectedMCPs.LIVER}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.LIVER.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("acute-ghvd-clinical-scoring.upper-gis")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("acute-ghvd-clinical-scoring.upper-gis")}
                  mcpId={cardData.mcpConfig.UPPER_GIS.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("UPPER_GIS", selected);
                    }
                  }}
                  value={selectedMCPs.UPPER_GIS}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.UPPER_GIS.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("acute-ghvd-clinical-scoring.lower-gis")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("acute-ghvd-clinical-scoring.lower-gis")}
                  mcpId={cardData.mcpConfig.LOWER_GIS.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("LOWER_GIS", selected);
                    }
                  }}
                  value={selectedMCPs.LOWER_GIS}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.LOWER_GIS.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("acute-ghvd-clinical-scoring.other")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("acute-ghvd-clinical-scoring.score")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={score}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default AcuteGvhdClinicalScoringCreate;
